import React, { useRef, useState } from 'react';
import { Tooltip } from '@/components/ui/tooltips/Tooltip';
import { colors } from '@/assets/styles/colors';


export interface BarChartDataPoint {
  label: string;
  value: number;
  isHighlighted?: boolean;
}

interface BarChartProps {
  data: BarChartDataPoint[];
  barColor?: string;
  highlightColor?: string;
  height?: number;
  formatTooltipValue?: (value: number) => string;
  'data-testid'?: string;
}

// Layout constants (pixels), except bar width which is CSS-driven so the chart fills
// whatever width its container (BarChartCard) allocates — no charting library exists in
// the project, same "no library" approach as DonutChart.tsx, but built with flex/CSS
// instead of a fixed-size SVG so it stays responsive across card widths.
const GRID_FRACTIONS = [0, 0.25, 0.5, 0.75, 1];
const DEFAULT_MAX_SCALE = 10;
const AXIS_LABEL_WIDTH = 28;
const BARS_GAP = 16;
const MAX_BAR_WIDTH = 36;
const TOP_PADDING = 10;
const BOTTOM_LABEL_HEIGHT = 28;

// Generic bar chart — purely presentational. Receives already-resolved data points
// (label/value/isHighlighted) and draws them; callers own all business logic (which
// month is highlighted, value formatting, colors). No threshold logic, no domain
// knowledge, same separation of concerns as DonutChart.tsx.
export const BarChart: React.FC<BarChartProps> = ({
  data,
  barColor = colors.primary.main,
  highlightColor = colors.primary.main,
  height = 200,
  formatTooltipValue,
  'data-testid': dataTestId,
}) => {
  const barRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const rawMax = Math.max(...data.map((point) => point.value), 0);
  // "Nice" scale: round up to the next multiple of 10, minimum 10 so a chart with no
  // values (or only small ones) never divides by zero and always shows some grid range.
  const maxScale = rawMax > 0 ? Math.ceil(rawMax / 10) * 10 : DEFAULT_MAX_SCALE;
  const barsAreaHeight = Math.max(height - TOP_PADDING - BOTTOM_LABEL_HEIGHT, 0);

  return (
    <div style={{ width: '100%' }} data-testid={dataTestId}>
      <div style={{ display: 'flex', width: '100%', height: barsAreaHeight + TOP_PADDING }}>
        <div
          style={{ position: 'relative', width: AXIS_LABEL_WIDTH, flexShrink: 0 }}
        >
          {GRID_FRACTIONS.map((fraction) => (
            <span
              key={fraction}
              style={{
                position: 'absolute',
                top: TOP_PADDING + barsAreaHeight * (1 - fraction),
                right: 6,
                transform: 'translateY(-50%)',
                fontFamily: 'Montserrat, sans-serif',
                fontSize: 10,
                color: colors.grays.neutral66,
                whiteSpace: 'nowrap',
              }}
            >
              {Math.round(maxScale * fraction)}
            </span>
          ))}
        </div>

        <div style={{ position: 'relative', flex: 1 }}>
          {GRID_FRACTIONS.map((fraction) => (
            <div
              key={fraction}
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: TOP_PADDING + barsAreaHeight * (1 - fraction),
                borderTop: `1px solid ${colors.grays.neutralEE}`,
              }}
            />
          ))}

          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: TOP_PADDING,
              height: barsAreaHeight,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-evenly',
              gap: BARS_GAP,
            }}
          >
            {data.map((point, index) => {
              const barHeightPercent = Math.min(100, Math.max(0, (point.value / maxScale) * 100));
              const fill = point.isHighlighted ? highlightColor : barColor;

              return (
                <div
                  key={index}
                  style={{
                    flex: 1,
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-end',
                  }}
                >
                  <div
                    ref={(element) => { barRefs.current[index] = element; }}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    onFocus={() => setHoveredIndex(index)}
                    onBlur={() => setHoveredIndex(null)}
                    tabIndex={0}
                    style={{
                      position: 'relative',
                      width: '100%',
                      maxWidth: MAX_BAR_WIDTH,
                      height: `${barHeightPercent}%`,
                      backgroundColor: fill,
                      opacity: point.isHighlighted ? 1 : 0.45,
                      borderRadius: '3px 3px 0 0',
                    }}
                    data-testid={`${dataTestId}-bar-${index}`}
                  >
                    {hoveredIndex === index && (
                      <Tooltip
                        body={formatTooltipValue
                          ? formatTooltipValue(point.value)
                          : String(point.value)}
                        anchorRef={{ current: barRefs.current[index] }}
                        strategy="absolute"
                        placement="top"
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', marginLeft: AXIS_LABEL_WIDTH, gap: BARS_GAP }}>
        {data.map((point, index) => (
          <div key={index} style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            {point.isHighlighted ? (
              <span
                style={{
                  backgroundColor: colors.primary.background,
                  borderRadius: 9,
                  padding: '2px 10px',
                }}
                data-testid={`${dataTestId}-label-chip-${index}`}
              >
                <span
                  style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: 10,
                    fontWeight: 600,
                    color: colors.primary.main,
                  }}
                  data-testid={`${dataTestId}-label-${index}`}
                >
                  {point.label}
                </span>
              </span>
            ) : (
              <span
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: 10,
                  fontWeight: 400,
                  color: colors.grays.neutral66,
                }}
                data-testid={`${dataTestId}-label-${index}`}
              >
                {point.label}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default BarChart;
